import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const categories = [
  { id: 'all', label: 'All Topics', icon: 'Grid' },
  { id: 'account', label: 'Account Management', icon: 'User' },
  { id: 'quizzes', label: 'Quizzes & Activities', icon: 'FileText' },
  { id: 'pronunciation', label: 'Pronunciation Tools', icon: 'Mic' },
  { id: 'technical', label: 'Technical Support', icon: 'Settings' }];


  const faqSections = [
  {
    category: 'account',
    title: 'Account Management',
    icon: 'User',
    faqs: [
    {
      question: 'How do I create a BardPlay account?',
      answer: 'To create an account, click the "Get Started" button on our homepage or navigate to the registration page. You\'ll need to provide your email, create a password, and select your role (student or educator). Students may need an invite code from their educator.'
    },
    {
      question: 'I forgot my password. How can I reset it?',
      answer: 'Click the "Forgot Password" link on the login page. Enter your email address, and we\'ll send you instructions to reset your password. Check your spam folder if you don\'t receive the email within a few minutes.'
    },
    {
      question: 'How do I update my profile information?',
      answer: 'Once logged in, click on your profile icon in the top right corner and select "Settings". From there, you can update your name, email, password, and other profile details.'
    },
    {
      question: 'Can I switch between student and educator accounts?',
      answer: 'Student and educator accounts are separate roles with different features. If you need both types of access, you\'ll need to create separate accounts. Contact support if you need assistance with role management.'
    }]

  },
  {
    category: 'quizzes',
    title: 'Quizzes & Activities',
    icon: 'FileText',
    faqs: [
    {
      question: 'How do I start a quiz?',
      answer: 'Navigate to your plays library, select a play you\'ve been reading, and look for the "Take Quiz" button. Quizzes are organized by act and scene to match your reading progress.'
    },
    {
      question: 'What types of questions are included in quizzes?',
      answer: 'BardPlay quizzes include multiple-choice questions about plot and characters, character matching exercises, and pronunciation challenges. Each quiz is designed to reinforce your understanding of the play.'
    },
    {
      question: 'Can I retake a quiz to improve my score?',
      answer: 'Yes! You can retake quizzes as many times as you\'d like. Your highest score will be recorded in your progress dashboard, and you can track your improvement over time.'
    },
    {
      question: 'How is my quiz performance calculated?',
      answer: 'Your performance is based on accuracy and completion. Each question type is weighted equally, and your overall score reflects the percentage of correct answers. Detailed results show your strengths and areas for improvement.'
    }]

  },
  {
    category: 'pronunciation',
    title: 'Pronunciation Tools',
    icon: 'Mic',
    faqs: [
    {
      question: 'How does the pronunciation feature work?',
      answer: 'Click the speaker icon next to any character name or difficult word to hear the correct pronunciation. Our audio recordings are performed by professional voice actors to ensure authentic Shakespearean pronunciation.'
    },
    {
      question: 'Can I record my own pronunciation attempts?',
      answer: 'Yes! During pronunciation quiz questions, you\'ll be prompted to record yourself saying the word or phrase. Our system will compare your recording to the correct pronunciation and provide feedback.'
    },
    {
      question: 'The pronunciation feature isn\'t working. What should I do?',
      answer: 'First, check that your browser has permission to access your microphone. Also ensure your device\'s volume is turned up. If problems persist, try using a different browser or contact our technical support team.'
    }]

  },
  {
    category: 'technical',
    title: 'Technical Troubleshooting',
    icon: 'Settings',
    faqs: [
    {
      question: 'Which browsers are supported?',
      answer: 'BardPlay works best on the latest versions of Chrome, Firefox, Safari, and Edge. For optimal performance, we recommend keeping your browser updated to the latest version.'
    },
    {
      question: 'Can I use BardPlay on my mobile device?',
      answer: 'Yes! BardPlay is fully responsive and works on smartphones and tablets. For the best mobile experience, we recommend using the latest version of your mobile browser.'
    },
    {
      question: 'Why is the page loading slowly?',
      answer: 'Slow loading can be caused by internet connectivity issues or browser cache. Try refreshing the page, clearing your browser cache, or checking your internet connection. If problems persist, contact support.'
    },
    {
      question: 'I\'m getting an error message. What should I do?',
      answer: 'First, try refreshing the page or logging out and back in. If the error persists, take a screenshot of the error message and contact our support team with details about what you were doing when the error occurred.'
    }]

  }];


  const gettingStartedGuides = [
  {
    role: 'student',
    title: 'Student Quick Start Guide',
    icon: 'BookOpen',
    steps: [
    'Create your account with an invite code from your educator',
    'Browse the plays library and select your first play',
    'Use the glossary to understand difficult words and phrases',
    'Complete quizzes after each act to test your knowledge',
    'Track your progress and earn achievements']

  },
  {
    role: 'educator',
    title: 'Educator Quick Start Guide',
    icon: 'Users',
    steps: [
    'Create your educator account and set up your profile',
    'Generate invite codes for your students',
    'Assign plays and set reading schedules',
    'Monitor student progress through the analytics dashboard',
    'Provide feedback and support as students learn']

  }];


  const videoTutorials = [
  {
    title: 'Navigating Your First Play',
    duration: '3:45',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a2bb5d00-1765361267868.png",
    description: 'Learn how to navigate through a Shakespearean play using BardPlay\'s intuitive interface'
  },
  {
    title: 'Completing Quizzes',
    duration: '4:20',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9e73807-1768118240235.png",
    description: 'Step-by-step guide to taking quizzes and understanding your results'
  },
  {
    title: 'Using the Glossary',
    duration: '2:30',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_10d44ea50-1767015072423.png",
    description: 'Discover how to use the glossary feature to understand archaic language'
  },
  {
    title: 'Tracking Your Progress',
    duration: '3:15',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6ac83f6-1766495162768.png",
    description: 'Explore your progress dashboard and achievement system'
  }];


  const commonIssues = [
  {
    issue: 'Cannot log in to my account',
    icon: 'AlertCircle',
    solution: 'Verify your email and password are correct. Use the "Forgot Password" feature if needed. Clear your browser cache and cookies, then try again.',
    relatedArticles: ['Password Reset Guide', 'Account Security Tips']
  },
  {
    issue: 'Quiz questions not loading',
    icon: 'AlertCircle',
    solution: 'Refresh your browser and ensure you have a stable internet connection. If the issue persists, try accessing the quiz from a different browser or device.',
    relatedArticles: ['Technical Requirements', 'Browser Compatibility']
  },
  {
    issue: 'Audio pronunciation not playing',
    icon: 'AlertCircle',
    solution: 'Check that your device volume is turned up and browser audio permissions are enabled. Try using headphones if available.',
    relatedArticles: ['Audio Troubleshooting', 'Device Settings']
  }];


  const filteredFAQs = faqSections?.filter((section) =>
  selectedCategory === 'all' || section?.category === selectedCategory
  )?.map((section) => ({
    ...section,
    faqs: section?.faqs?.filter((faq) =>
    searchQuery === '' ||
    faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    )
  }))?.filter((section) => section?.faqs?.length > 0);

  const toggleFAQ = (sectionIndex, faqIndex) => {
    const key = `${sectionIndex}-${faqIndex}`;
    setExpandedFAQ(expandedFAQ === key ? null : key);
  };

  return (
    <>
      <Helmet>
        <title>Help Center - BardPlay</title>
        <meta name="description" content="Find answers to common questions and get support for using BardPlay's Shakespeare learning platform" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/20 to-secondary/20 text-white py-16 border-b border-white/10">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">How Can We Help?</h1>
              <p className="text-xl text-white/90">Find answers, learn features, and get support</p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Icon name="Search" className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60" size={20} />
                <input
                  type="text"
                  placeholder="Search for help articles, features, or common issues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-sm text-white placeholder-white/60 shadow-lg focus:ring-2 focus:ring-primary focus:outline-none border border-white/20" />

              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="bg-surface-dark/80 backdrop-blur-sm border-b border-white/10 sticky top-0 z-10 shadow-sm">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-4">
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {categories?.map((category) =>
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                selectedCategory === category?.id ?
                'bg-primary text-white shadow-md' :
                'bg-white/10 text-white hover:bg-white/20 border border-white/20'}`
                }>

                  <Icon name={category?.icon} size={18} />
                  <span className="font-medium">{category?.label}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-12">
          {/* Getting Started Guides */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
              <Icon name="BookOpen" size={32} className="text-primary" />
              Getting Started Guides
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {gettingStartedGuides?.map((guide, index) =>
              <div key={index} className="bg-surface-dark/50 backdrop-blur-sm rounded-xl shadow-md p-8 border border-white/10 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <Icon name={guide?.icon} size={24} className="text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{guide?.title}</h3>
                  </div>
                  <ol className="space-y-4">
                    {guide?.steps?.map((step, stepIndex) =>
                  <li key={stepIndex} className="flex gap-3">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                          {stepIndex + 1}
                        </span>
                        <span className="text-white/80 pt-1">{step}</span>
                      </li>
                  )}
                  </ol>
                </div>
              )}
            </div>
          </section>

          {/* Video Tutorials */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
              <Icon name="Video" size={32} className="text-secondary" />
              Video Tutorials
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {videoTutorials?.map((video, index) =>
              <div key={index} className="bg-surface-dark/50 backdrop-blur-sm rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer border border-white/10">
                  <div className="relative group">
                    <img
                    src={video?.thumbnail}
                    alt={video?.title}
                    className="w-full h-48 object-cover" />

                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                        <Icon name="Play" size={32} className="text-primary ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                      {video?.duration}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-white mb-2">{video?.title}</h3>
                    <p className="text-sm text-white/70">{video?.description}</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* FAQ Sections */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
              <Icon name="HelpCircle" size={32} className="text-green-500" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-8">
              {filteredFAQs?.map((section, sectionIndex) =>
              <div key={sectionIndex} className="bg-surface-dark/50 backdrop-blur-sm rounded-xl shadow-md p-8 border border-white/10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <Icon name={section?.icon} size={20} className="text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{section?.title}</h3>
                  </div>
                  <div className="space-y-4">
                    {section?.faqs?.map((faq, faqIndex) =>
                  <div key={faqIndex} className="border-b border-white/10 last:border-0 pb-4 last:pb-0">
                        <button
                      onClick={() => toggleFAQ(sectionIndex, faqIndex)}
                      className="w-full flex items-start justify-between gap-4 text-left group">

                          <span className="font-semibold text-white group-hover:text-primary transition-colors">
                            {faq?.question}
                          </span>
                          <Icon
                        name={expandedFAQ === `${sectionIndex}-${faqIndex}` ? "ChevronUp" : "ChevronDown"}
                        size={20}
                        className="text-white/60 flex-shrink-0 mt-1" />

                        </button>
                        {expandedFAQ === `${sectionIndex}-${faqIndex}` &&
                    <div className="mt-3 text-white/80 leading-relaxed pl-0">
                            {faq?.answer}
                          </div>
                    }
                      </div>
                  )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Common Issues */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
              <Icon name="AlertCircle" size={32} className="text-orange-500" />
              Common Issues & Solutions
            </h2>
            <div className="grid lg:grid-cols-3 gap-6">
              {commonIssues?.map((item, index) =>
              <div key={index} className="bg-surface-dark/50 backdrop-blur-sm rounded-xl shadow-md p-6 border border-white/10 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={20} className="text-orange-500" />
                    </div>
                    <h3 className="font-bold text-white">{item?.issue}</h3>
                  </div>
                  <p className="text-white/80 mb-4">{item?.solution}</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="text-sm font-semibold text-white/70 mb-2">Related Articles:</p>
                    <ul className="space-y-1">
                      {item?.relatedArticles?.map((article, idx) =>
                    <li key={idx}>
                          <a href="#" className="text-sm text-primary hover:underline flex items-center gap-1">
                            <Icon name="ArrowRight" size={14} />
                            {article}
                          </a>
                        </li>
                    )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Contact Support */}
          <section className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl shadow-xl p-12 text-center text-white backdrop-blur-sm border border-white/10">
            <Icon name="MessageCircle" size={48} className="mx-auto mb-4 opacity-90" />
            <h2 className="text-3xl font-bold mb-4">Still Need Help?</h2>
            <p className="text-xl mb-8 text-white/90">
              Our support team is here to assist you with any questions or issues
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact-us"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold border border-white/20 transition-colors">

                <Icon name="Mail" size={20} />
                Contact Support
              </a>
              <a
                href="#"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold transition-colors">

                <Icon name="MessageCircle" size={20} />
                Live Chat
              </a>
            </div>
          </section>
        </div>
      </div>
    </>);

};

export default HelpCenter;